import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Search, Star, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import UserAvatar from "@/components/shared/Avatar";
import { cn } from "@/lib/utils";

export interface ContactCandidate {
  id: string;
  name: string;
  email?: string;
  company?: string;
  title?: string;
  avatar?: string;
}

interface Props {
  /** Ordered contact names — first is the primary. */
  contacts: string[];
  /** All searchable people (used for the Add typeahead). */
  candidates: ContactCandidate[];
  onChange: (contacts: string[]) => void;
  onDrillDown?: (candidate: ContactCandidate) => void;
}

/**
 * PREVIEW-ONLY cell. This component is name-keyed: it stores contacts as names
 * and resolves them against `candidates`. When a name is not in `candidates` it
 * hands `onDrillDown` a synthesized `{ id: name, name }` — an id slot holding a
 * display name. That is only safe while `candidates` is the dummy people list
 * and names are guaranteed to resolve.
 *
 * Do NOT render this against real workspace data: real deal contacts miss the
 * dummy candidate list, so the fabricated id reaches the backend and resolves
 * to nothing (getContact -> null, every tab in the person sheet empty).
 * Production paths use EditableContacts, which is id-keyed and searches people
 * over the backend. See DealsPage / DealDetailSheet "contacts" for both branches.
 */

export default function MultiContactCell({ contacts, candidates, onChange, onDrillDown }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setSearch("");
    }
  }, [open]);

  const byName = useMemo(() => {
    const m = new Map<string, ContactCandidate>();
    for (const c of candidates) if (!m.has(c.name)) m.set(c.name, c);
    return m;
  }, [candidates]);

  const primary = contacts[0] || "";
  const extra = contacts.length > 1 ? contacts.length - 1 : 0;
  const primaryCand = byName.get(primary);

  const setPrimary = (idx: number) => {
    if (idx === 0) return;
    const item = contacts[idx];
    onChange([item, ...contacts.filter((_, i) => i !== idx)]);
  };
  const removeContact = (idx: number) => onChange(contacts.filter((_, i) => i !== idx));
  const addContact = (name: string) => {
    if (!name || contacts.includes(name)) return;
    onChange([...contacts, name]);
    setSearch("");
  };

  const results = search.trim()
    ? candidates
        .filter(c => !contacts.includes(c.name) && c.name.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 8)
    : [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-1.5 group min-w-[140px]">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {primary ? (
            <>
              <UserAvatar name={primary} size="xs" src={primaryCand?.avatar} />
              <button
                type="button"
                className="text-sm truncate hover:text-accent transition text-left"
                onClick={e => {
                  e.stopPropagation();
                  onDrillDown?.(primaryCand ?? { id: primary, name: primary });
                }}
              >
                {primary}
              </button>
              {extra > 0 && (
                <button
                  type="button"
                  className="shrink-0 text-[10px] font-medium bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 leading-none hover:bg-muted/70 transition"
                  onClick={e => { e.stopPropagation(); setOpen(true); }}
                  title={t("grid.cell.showMore", { extra })}
                >
                  +{extra}
                </button>
              )}
            </>
          ) : (
            <span className="text-sm text-muted-foreground">-</span>
          )}
        </div>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t("grid.cell.editContacts")}
            className="shrink-0 rounded p-0.5 hover:bg-muted/40 transition opacity-0 group-hover:opacity-100"
            onClick={e => e.stopPropagation()}
          >
            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent align="start" className="w-80 p-3 z-[70]" onClick={e => e.stopPropagation()}>
        <div className="space-y-1.5 mb-3">
          {contacts.length === 0 && (
            <p className="text-xs text-muted-foreground">{t("grid.cell.noContacts")}</p>
          )}
          {contacts.map((name, idx) => {
            const c = byName.get(name);
            return (
              <div key={`${name}-${idx}`} className="flex items-center justify-between gap-2 group/item">
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <UserAvatar name={name} size="xs" src={c?.avatar} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setOpen(false);
                          onDrillDown?.(c ?? { id: name, name });
                        }}
                        className={cn(
                          "text-sm truncate text-left hover:text-accent transition",
                          idx === 0 && "font-medium",
                        )}
                      >
                        {name}
                      </button>
                      {idx === 0 && (
                        <span className="shrink-0 text-[10px] font-medium bg-accent/10 text-accent rounded px-1.5 py-0.5 leading-none">{t("grid.cell.primary")}</span>
                      )}
                    </div>
                    {c?.title && <div className="text-[11px] text-muted-foreground truncate">{c.title}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {idx !== 0 && (
                    <button
                      onClick={() => setPrimary(idx)}
                      className="opacity-0 group-hover/item:opacity-100 transition text-muted-foreground hover:text-accent"
                      title={t("grid.cell.makePrimary")}
                    >
                      <Star className="h-3 w-3" />
                    </button>
                  )}
                  <button
                    onClick={() => removeContact(idx)}
                    className="opacity-0 group-hover/item:opacity-100 transition text-muted-foreground hover:text-destructive"
                    title={t("grid.cell.remove")}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="border-t border-border pt-2">
          <div className="relative">
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t("grid.cell.searchContacts")}
              className="w-full bg-transparent border-b border-accent outline-none text-sm py-1 pl-5"
            />
          </div>
          {search.trim() && (
            <div className="mt-1 max-h-48 overflow-y-auto rounded-md border border-border bg-popover shadow-md py-1">
              {results.length === 0 ? (
                <div className="px-3 py-2 text-xs text-muted-foreground">{t("grid.cell.noMatches")}</div>
              ) : (
                results.map(c => (
                  <button
                    key={c.id}
                    onMouseDown={e => { e.preventDefault(); addContact(c.name); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition flex items-center gap-2"
                  >
                    <UserAvatar name={c.name} size="xs" src={c.avatar} />
                    <span className="truncate flex-1">{c.name}</span>
                    {c.company && <span className="text-[11px] text-muted-foreground truncate">{c.company}</span>}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}